import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/stores/user'

const chatEnabled = (import.meta as any).env?.VITE_CHAT_ENABLED === 'true'

// 路由配置
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Layout',
    component: () => import('@/layouts/DefaultLayout.vue'),
    children: [
      {
        path: '',
        name: 'Home',
        component: () => import('@/views/Home.vue'),
        meta: {
          title: '首页',
        },
      },
      {
        path: '/products',
        name: 'Products',
        component: () => import('@/views/Products.vue'),
        meta: {
          title: '商品列表',
        },
      },
      {
        path: '/product/:id',
        name: 'ProductDetail',
        component: () => import('@/views/ProductDetail.vue'),
        meta: {
          title: '商品详情',
        },
      },
      {
        path: '/cart',
        name: 'Cart',
        component: () => import('@/views/Cart.vue'),
        meta: {
          title: '购物车',
          requiresAuth: true,
        },
      },
      {
        path: '/checkout',
        name: 'Checkout',
        component: () => import('@/views/Checkout.vue'),
        meta: {
          title: '结算',
          requiresAuth: true,
        },
      },
      {
        path: '/checkout/pay/:orderId',
        name: 'CheckoutPay',
        component: () => import('@/views/CheckoutPay.vue'),
        meta: {
          title: '订单支付',
          requiresAuth: true,
        },
      },
      {
        path: '/orders/:id',
        name: 'OrderDetail',
        component: () => import('@/views/OrderDetail.vue'),
        meta: {
          title: '订单详情',
          requiresAuth: true,
        },
      },
      {
        path: '/profile',
        name: 'Profile',
        component: () => import('@/views/Profile.vue'),
        meta: {
          title: '个人中心',
          requiresAuth: true,
        },
      },
      ...(chatEnabled
        ? ([
            {
              path: '/chat',
              name: 'Chat',
              component: () => import('@/views/Chat.vue'),
              meta: {
                title: '在线客服',
                requiresAuth: true,
              },
            },
          ] as RouteRecordRaw[])
        : []),
      {
        path: '/orders',
        name: 'Orders',
        component: () => import('@/views/Orders.vue'),
        meta: {
          title: '我的订单',
          requiresAuth: true,
        },
      },
      {
        path: '/news',
        name: 'News',
        component: () => import('@/views/News.vue'),
        meta: {
          title: '新闻资讯',
        },
      },
      {
        path: '/news/:id',
        name: 'NewsDetail',
        component: () => import('@/views/NewsDetail.vue'),
        meta: {
          title: '新闻详情',
        },
      },
      {
        path: '/services',
        name: 'Services',
        component: () => import('@/views/Services.vue'),
        meta: {
          title: '服务支持',
        },
      },
      {
        path: '/contact',
        name: 'Contact',
        component: () => import('@/views/Contact.vue'),
        meta: {
          title: '联系我们',
        },
      },
      {
        path: '/about',
        name: 'About',
        component: () => import('@/views/About.vue'),
        meta: {
          title: '关于我们',
        },
      },
    ],
  },
  {
    path: '/auth',
    name: 'Auth',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      {
        path: 'login',
        name: 'Login',
        component: () => import('@/views/auth/Login.vue'),
        meta: {
          title: '登录',
        },
      },
      {
        path: 'register',
        name: 'Register',
        component: () => import('@/views/auth/Register.vue'),
        meta: {
          title: '注册',
        },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: {
      title: '页面不存在',
    },
  },
]

// 创建路由实例
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
})

// 路由守卫
router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore()
  
  // 设置页面标题
  const title = to.meta.title as string
  if (title) {
    document.title = `${title} - MIER商城`
  }
  
  // 检查是否需要登录
  if (to.meta.requiresAuth && !userStore.isAuthenticated) {
    // 未登录时跳转到登录页
    next({
      name: 'Login',
      query: { redirect: to.fullPath },
    })
    return
  }
  
  // 如果已登录访问认证页面，重定向到首页
  if (to.path.startsWith('/auth') && userStore.isAuthenticated) {
    next({ name: 'Home' })
    return
  }
  
  next()
})

export default router
